import {
    InteractionAggregatesBucket,
    IVoice,
    ProcessedInteractionAggregatesBucket,
    Providers,
    TalkPresenter,
    VideoType,
    VoiceAccess,
} from '@d-id/types';
import { AGENT_DEFAULT_NAME, OTHERS_TOPICS } from '../constants';

export const convertURL = (url: string) => (url.match(/^https?:\/\//) ? url : 'https://' + url).replace(/\/+$/, '');

export const creditsToAgentMinutes = (credits: number): number => {
    return credits / 2;
};

export const isKeyInUserLocalStorage = (email: string | undefined, key: string) => {
    if (!email) {
        return false;
    }
    const storedUserKeys = localStorage.getItem(email);
    const parsedStoredUserKeys: Record<string, any> = storedUserKeys ? JSON.parse(storedUserKeys) : {};
    return !!parsedStoredUserKeys[key];
};

export const setKeyInUserLocalStorage = (email: string | undefined, key: string): void => {
    if (!!email) {
        const storedUserKeys = localStorage.getItem(email);
        const parsedStoredUserKeys: Record<string, any> = storedUserKeys ? JSON.parse(storedUserKeys) : {};
        parsedStoredUserKeys[key] = true;
        localStorage.setItem(email, JSON.stringify(parsedStoredUserKeys));
    }
};

export const defaultAgentGreeting = (agentName: string) =>
    `Hi! I'm ${agentName !== '' ? agentName : AGENT_DEFAULT_NAME}. How can I help you?`;

export const processInsightsAggregates = (buckets: InteractionAggregatesBucket[]): ProcessedInteractionAggregatesBucket[] => {
    const totalInteractions = buckets.reduce((acc, bucket) => acc + bucket.n_interactions || 0, 0);
    const sortedBuckets = buckets
        .sort((a, b) => b.n_users || 0 - a.n_users || 0)
        .map(bucket => {
            return {
                ...bucket,
                users_change: calculatePercentageChange(bucket.n_users, bucket.n_users_prev),
                interactions_change: calculatePercentageChange(bucket.n_interactions, bucket.n_interactions_prev),
                conversations_change: calculatePercentageChange(bucket.n_sessions, bucket.n_sessions_prev),
                total_interactions: calculatePercentage(bucket.n_interactions / totalInteractions),
                session_time_change: calculatePercentageChange(bucket.session_time, bucket.session_time_prev),
                topics: bucket.topics && bucket.topics.length > 0 ? bucket.topics : [OTHERS_TOPICS],
            } as ProcessedInteractionAggregatesBucket;
        });

    return sortedBuckets;
};

export const calculatePercentageChange = (current?: number, previous?: number): number | undefined => {
    if (!current && previous) {
        return -100;
    }
    if (!current || !previous) {
        return 0;
    }
    return calculatePercentage((current - previous) / previous);
};

export const calculatePercentage = (num: number) => {
    return Number((num * 100).toFixed(1));
};

export const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export const calculateSentiment = (value: number): 'positive' | 'negative' | 'neutral' => {
    if (value > 0) {
        return 'positive';
    }
    if (value < 0) {
        return 'negative';
    }

    return 'neutral';
};

export const displayInPlayground = (imageUrl?: string) => {
    if (!imageUrl) {
        return;
    }

    if (typeof window.DID_AGENTS_API?.configure === 'function') {
        window.DID_AGENTS_API.configure({ posterSrc: imageUrl });
    }
};

export const isTalkPresenter = (presenter: any): presenter is TalkPresenter => {
    return presenter && presenter.type === VideoType.TALK;
};

export const isMsDeprecatedVoice = (voice: IVoice | null): boolean => {
    return !!voice && voice.provider === Providers.Microsoft && !voice.id.endsWith('Neural');
};

export function updateSelectedVoice(callback?: (voice: IVoice) => void, voiceId?: string, options: IVoice[] = [], gender?: string) {
    const voice =
        options.find(voice => voice.id === voiceId) ||
        options.find(voice => voice.access !== VoiceAccess.Premium && (!gender || voice.gender === gender));
    if (voice) {
        callback?.(voice);
    }
    return voice;
}
